import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["linkText"],
  _excluded2 = ["href"];
// @ts-expect-error tinymce not typed

import { hasHublVariable } from 'rich-text-lib/utils/hubl';
import { restoreFocusAfter } from 'rich-text-lib/utils/dom';
import { LinkTypes } from 'rich-text-lib/constants/link';
import { PopoverType } from 'rich-text-lib/constants/popover';
import { hasValidUrlScheme } from './url';
import { getFlatChildNodes } from './dom';
import { savedLinkFields, LINK_TARGET_BLANK_KEY } from '../constants/hslink';
import { isAnchorNode } from './anchor';
import { replaceNodes } from './transformer';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module '../h... Remove this comment to see the full error message
import { replacePlaceholderWithHubl } from '../hspersonalize/shared';

// @TODO: DomQuery (editor.$) is deprecated and removed in open source tinymce@6

export const LinkActions = Object.assign({}, PopoverType);
const ValidTextElements = ['SPAN', 'STRONG', 'EM', 'SUP', 'SUB', '#text'];
const isEmptyLink = link => link === '' || link === 'mailto:';
export function anchorNodeContainsOnlyTextElements(linkNode) {
  if (!linkNode || linkNode.nodeName !== 'A') return false;
  const childNodes = getFlatChildNodes(linkNode);
  return childNodes.reduce((acc, child) => acc && ValidTextElements.includes(child.nodeName), true);
}
export function nodeIsLinkNode(node) {
  if (!node) return false;
  return node.nodeType === Node.ELEMENT_NODE && node.tagName === 'A' && !isAnchorNode(node);
}
export function isNodeWithinLink(editor, selectedNode) {
  if (!selectedNode) return false;
  const anchorNode = editor.dom.getParent(selectedNode, 'a', editor.getBody());
  const isLink = anchorNode && anchorNode.hasAttribute('href');
  const isRssLink = anchorNode && anchorNode.hasAttribute('data-mce-rss-link');
  return isLink && !isRssLink;
}
export function isNodeWithinTempLink(editor, selectedNode) {
  if (!selectedNode) return false;
  const anchorNode = editor.dom.getParent(selectedNode, 'a', editor.getBody());
  return anchorNode && anchorNode.hasAttribute('data-insert');
}
export function getSelectedLink($selection) {
  return $selection.is('a') ? $selection : $selection.parents('a').first();
}
export function convertRootRelLinkToAbsUrl(editor, href) {
  if (!editor.settings.contentDomain) {
    return href;
  }
  const protocol = editor.settings.domainIsSsl ? 'https' : 'http';
  return `${protocol}://${editor.settings.contentDomain}${href}`;
}
export function convertSamePageLinkToAbsUrl(editor, href) {
  if (!editor.settings.absoluteUrl) {
    return href;
  }
  return `${editor.settings.absoluteUrl}${href}`;
}
export function convertPageRelLinkToAbsUrl(editor, href) {
  if (!editor.settings.absoluteUrl) {
    return href;
  }
  return `${editor.settings.absoluteUrl}/${href}`;
}
export function getAbsoluteOrMailtoLink(editor, href) {
  if (hasValidUrlScheme(href) || hasHublVariable(href) || href[0] === '#') {
    return href;
  } else if (href[0] === '/') {
    return convertRootRelLinkToAbsUrl(editor, href);
  }
  return convertPageRelLinkToAbsUrl(editor, href);
}
const editAnchorAttributes = (editor, parentAnchor, linkText, linkAttrs) => {
  editor.undoManager.transact(() => {
    // remove data-hs-domains-rewrite. It will be added back below
    // if still applicable
    parentAnchor.removeAttribute(savedLinkFields.DOMAIN_REWRITE_ATTR);
    // remove data-insert for cases where it is not removed prior to insert
    parentAnchor.removeAttribute('data-insert');
    editor.dom.setAttribs(parentAnchor, linkAttrs);
    if (linkText && linkText.trim() && parentAnchor.innerText !== linkText) {
      parentAnchor.textContent = linkText;
      editor.selection.select(parentAnchor);
    }
  });
};

// TODO: Can we support relative urls for pages that have NOT been published?
function getPreviewHref(editor, href) {
  if (href && href[0] === '#') return convertSamePageLinkToAbsUrl(editor, href);
  return href;
}
export function insertOrEditLink(editor, target, savedLinkInfo, action) {
  const {
    dom
  } = editor;
  const {
      linkText
    } = savedLinkInfo,
    linkAttrs = _objectWithoutPropertiesLoose(savedLinkInfo, _excluded);
  if (action === LinkActions.EDIT) {
    if (!target || target.nodeName !== 'A') return;
    if (linkText && linkText.trim() && target.innerText !== linkText) {
      restoreFocusAfter(() => {
        editAnchorAttributes(editor, target, linkText, linkAttrs);
      });
    } else {
      editAnchorAttributes(editor, target, linkText, linkAttrs);
    }
  } else if (action === LinkActions.INSERT) {
    editor.undoManager.transact(() => {
      const finalLinkAttrs = Object.assign({}, linkAttrs, {
        id: '__hsNewLink'
      });
      if (target && target.nodeName === 'A') {
        editor.execCommand('mceInsertLink', false, finalLinkAttrs);
      } else {
        const linkHTML = dom.createHTML('a', finalLinkAttrs, dom.encode(linkText));
        editor.insertContent(linkHTML);
      }

      // Select the newly inserted link node
      const linkNode = editor.dom.get('__hsNewLink');
      editor.dom.setAttrib(linkNode, 'id', null);
      editor.selection.select(linkNode);
    });
  }
}

// removes all links that were in the process of being inserted but didn't go through
// since formatter can't be used on an arbitrary selection but can be on an arbitrary node,
// this seems like the best solution to account for multi-node selection without changing the way formatter works
export const removeUnInsertedLinks = editor => () => {
  const unInsertedLinks = editor.dom.select('a[data-insert]');
  Array.from(unInsertedLinks).forEach(node => {
    if (node.getAttribute('href')) {
      editor.dom.setAttrib(node, 'data-insert', null);
    } else {
      editor.formatter.remove('link', {}, node);
    }
  });
};
export const removeLink = (editor, target) => {
  editor.undoManager.transact(() => {
    editor.formatter.remove('link', {}, target);
    editor.selection.select(target);
  });
};
const addLink = (editor, target, linkInfo) => {
  editor.undoManager.transact(() => {
    editor.formatter.apply('link', linkInfo);
    editor.selection.select(target);
  });
};
export const updateLinkInfo = (editor, target, linkInfo) => {
  const parentAnchor = editor.dom.getParent(target, 'a');
  if (parentAnchor) {
    if (isEmptyLink(linkInfo.href)) {
      removeLink(editor, target);
    } else {
      editor.undoManager.transact(() => {
        // remove data-hs-domain-rewrite. It will be added back via next line
        // if still applicable
        parentAnchor.removeAttribute(savedLinkFields.DOMAIN_REWRITE_ATTR);
        editor.dom.setAttribs(parentAnchor, linkInfo);
      });
    }
  } else if (!isEmptyLink(linkInfo.href)) {
    addLink(editor, target, linkInfo);
  }
};
export const getLinkAttrProps = (editor, $selectedLink, allowRawLinksOnly) => {
  const href = $selectedLink.attr('href') && getAbsoluteOrMailtoLink(editor, $selectedLink.attr('href'));
  let linkText;
  if (allowRawLinksOnly) {
    const clonedLink = $selectedLink.clone();
    replaceNodes(clonedLink.children().filter('img[data-mce-property-name]:not([data-mce-rss-link])'), replacePlaceholderWithHubl());
    linkText = clonedLink.text();
  } else {
    linkText = $selectedLink.text();
  }
  const linkAttrProps = {
    href,
    previewHref: getPreviewHref(editor, href),
    externalLink: $selectedLink.attr('target') === LINK_TARGET_BLANK_KEY,
    linkText,
    relAttr: $selectedLink.attr('rel') ? $selectedLink.attr('rel').trim().split(' ') : []
  };
  return linkAttrProps;
};
export const adaptLinkPropsToAttr = (linkInfo, options = {}) => {
  const {
    href,
    relAttr,
    externalLink,
    linkText,
    linkType
  } = linkInfo;
  const {
    allowRawLinksOnly,
    omitDomainRewriteAttr
  } = options;
  const shouldAddDomainRewriteAttr = !omitDomainRewriteAttr && linkType === LinkTypes.FILE;
  if (!relAttr.includes('noopener')) {
    relAttr.push('noopener');
  }
  const rel = relAttr.join(' ');
  let text = allowRawLinksOnly ? href : linkText;
  if (allowRawLinksOnly && text.startsWith('mailto:')) {
    text = text.replace('mailto:', '');
  }
  return Object.assign({
    [savedLinkFields.HREF]: href,
    [savedLinkFields.REL]: rel,
    [savedLinkFields.TARGET]: externalLink ? LINK_TARGET_BLANK_KEY : null,
    [savedLinkFields.TEXT]: text
  }, shouldAddDomainRewriteAttr && {
    [savedLinkFields.DOMAIN_REWRITE_ATTR]: true
  });
};
export const cleanLinkAttributes = linkInfo => {
  const {
      href
    } = linkInfo,
    rest = _objectWithoutPropertiesLoose(linkInfo, _excluded2);
  return Object.assign({
    href: href || ''
  }, rest);
};
export const bindHrefAndLinkText = node => {
  // Preserve "mailto:" links
  let maybePrefix = '';
  const _href = node.getAttribute('href');
  if (_href && _href.startsWith('mailto:')) {
    maybePrefix = 'mailto:';
  }
  const clonedLink = node.cloneNode(true);
  replaceNodes(clonedLink.querySelectorAll('img[data-mce-property-name]:not([data-mce-rss-link])'), replacePlaceholderWithHubl());
  const href = `${maybePrefix}${clonedLink.innerHTML}`;
  node.setAttribute('href', href);
  node.dataset.mceHref = href;
};
export const unlinkIfInvalid = editor => {
  const {
    textContainsValidLink
  } = editor.settings;
  const parent = editor.dom.getParent(editor.selection.getNode(), 'a[href]');
  if (parent && typeof textContainsValidLink === 'function' && !textContainsValidLink(parent.innerText)) {
    editor.undoManager.ignore(() => {
      const bookmark = editor.selection.getBookmark();
      editor.execCommand('unlink');
      editor.selection.moveToBookmark(bookmark);
    });
  }
};